import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useIsMobile } from '@/hooks/use-mobile';
import { cn } from '@/lib/utils';

interface FloatingToolbarProps {
  position: { x: number; y: number } | null;
  onImprove: () => void;
  onReset: () => void;
  onEdit: () => void;
  status: 'idle' | 'loading' | 'done';
}

export function FloatingToolbar({ position, onImprove, onReset, onEdit, status }: FloatingToolbarProps) {
  const isMobile = useIsMobile();
  const isVisible = position !== null;

  return (
    <AnimatePresence mode="wait">
      {isVisible && (
        <motion.div
          id="floating-toolbar"
          key="floating-toolbar"
          initial={isMobile ? { opacity: 0, y: 100 } : { opacity: 0, y: -20, scale: 0.95 }}
          animate={isMobile ? { opacity: 1, y: 0 } : { opacity: 1, y: 0, scale: 1 }}
          exit={isMobile ? { opacity: 0, y: 100 } : { opacity: 0, scale: 0.95, transition: { duration: 0.15 } }}
          transition={{ duration: 0.2, ease: "easeOut" }}
          style={isMobile ? {
            position: 'fixed',
            bottom: '24px',
            left: '24px',
            right: '24px',
            zIndex: 999
          } : {
            position: 'absolute',
            top: position!.y ?? 0,
            left: position!.x ?? 0,
            zIndex: 999
          }}
          className={cn(
            "flex items-center bg-inverse-surface rounded-xl px-2 py-1.5 shadow-2xl z-20 gap-1 border border-outline-variant/30 transition-all duration-500",
            status === 'done' && "forged-success-glow"
          )}
          role="toolbar"
          aria-label="Editorial Assistant"
        >
          <button
            onClick={onEdit}
            type="button"
            className="flex items-center gap-1.5 px-3 py-1.5 text-inverse-on-surface hover:bg-inverse-on-surface/10 rounded-lg transition-colors group"
          >
            <span className="material-symbols-outlined text-[18px] group-hover:scale-110 transition-transform">edit</span>
            <span className="font-label-caps text-[11px] uppercase tracking-wider">Edit</span>
          </button>
          
          <div className="w-px h-4 bg-inverse-on-surface/20 mx-0.5" />
          
          <button
            onClick={onImprove}
            disabled={status === 'loading'}
            type="button"
            className={cn(
              "flex items-center gap-1.5 px-3 py-1.5 text-inverse-on-surface hover:bg-inverse-on-surface/10 rounded-lg transition-all group min-w-[90px] justify-center",
              status === 'loading' && "animate-pulse"
            )}
          >
            {status === 'loading' ? (
              <span className="material-symbols-outlined text-[18px] animate-spin">progress_activity</span>
            ) : (
              <span className="material-symbols-outlined text-[18px] text-primary group-hover:rotate-12 transition-transform">auto_awesome</span>
            )}
            <span className="font-label-caps text-[11px] uppercase tracking-wider">
              {status === 'loading' ? 'Forging' : status === 'done' ? 'Forged' : 'Improve'}
            </span>
          </button>
          
          <div className="w-px h-4 bg-inverse-on-surface/20 mx-0.5" />
          
          <button
            onClick={onReset}
            type="button"
            className="flex items-center gap-1.5 px-3 py-1.5 text-inverse-on-surface hover:bg-inverse-on-surface/10 rounded-lg transition-all group"
            title="Reset to original"
          >
            <span className="material-symbols-outlined text-[18px] group-hover:-rotate-45 transition-transform">undo</span>
            <span className="font-label-caps text-[11px] uppercase tracking-wider">Reset</span>
          </button>
        </motion.div>
      )}
    </AnimatePresence>
  );
}